import React, { useMemo, useState } from "react";
import styled from "styled-components";
import Calendar from "../../organisms/Calendar";
import {
  ListaAsistenciaProyectada,
  esAsistenciaPasada,
} from "../../../services/asistenciaServicios";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import { Card, Col, Row } from "react-bootstrap";
import CardTitle from "../../organisms/CardTitle";
import { Typeahead } from "react-bootstrap-typeahead";
import ConvencionColores from "./ConvencionColores";
import LoadingScreen from "../../organisms/LoadingScreen";
import { stringADate } from "../../../utils/dateUtils";

function CalendarSection({
  listasProyectadasOrdenadas,
  listasAsistenciaProyectadasError,
  listasAsistenciaProyectadasIsLoading,
  onDateClick,
}: {
  listasProyectadasOrdenadas: ListaAsistenciaProyectada[];
  listasAsistenciaProyectadasError: unknown;
  listasAsistenciaProyectadasIsLoading: boolean;
  onDateClick: (date: Date) => void;
}) {
  const [mascotaFiltro, setMascotaFiltro] = useState<
    MascotaProyectada | undefined
  >(undefined);

  const fechasAgrupadasPorIdMascota = useMemo(() => {
    return agruparFechasPorIdMascota(listasProyectadasOrdenadas);
  }, [listasProyectadasOrdenadas]);

  const fechasFiltradas = useMemo(() => {
    let fechas: string[] = [];
    if (mascotaFiltro) {
      fechas = Array.from(
        fechasAgrupadasPorIdMascota.get(mascotaFiltro.id) || new Set()
      );
    } else {
      fechas = listasProyectadasOrdenadas.map((lista) => lista.fecha);
    }

    return fechas;
  }, [listasProyectadasOrdenadas, fechasAgrupadasPorIdMascota, mascotaFiltro]);

  const fechasFiltradasDate = useMemo(() => {
    return fechasFiltradas.map(f => stringADate(f));
  }, [fechasFiltradas]);

  const fechasBajas = useMemo(() => {
    return listasProyectadasOrdenadas
      .filter((lista) => lista.nivel === "BAJO")
      .map((lista) => lista.fecha)
      .filter((fecha) => fechasFiltradas.includes(fecha));
  }, [listasProyectadasOrdenadas, fechasFiltradas]);

  const fechasBajasDate = useMemo(() => {
    return fechasBajas.map(f => stringADate(f));
  }, [fechasBajas]);

  const fechasMedias = useMemo(() => {
    return listasProyectadasOrdenadas
      .filter((lista) => lista.nivel === "MEDIO")
      .map((lista) => lista.fecha)
      .filter((fecha) => fechasFiltradas.includes(fecha));
  }, [listasProyectadasOrdenadas, fechasFiltradas]);

  const fechasMediasDate = useMemo(() => {
    return fechasMedias.map(f => stringADate(f));
  }, [fechasMedias]);

  const fechasAltas = useMemo(() => {
    return listasProyectadasOrdenadas
      .filter((lista) => lista.nivel === "ALTO")
      .map((lista) => lista.fecha)
      .filter((fecha) => fechasFiltradas.includes(fecha));
  }, [listasProyectadasOrdenadas, fechasFiltradas]);

  const fechasAltasDate = useMemo(() => {
    return fechasAltas.map(f => stringADate(f));
  }, [fechasAltas]);

  const fechasConGuarderia = useMemo(() => {
    return listasProyectadasOrdenadas
      .filter((lista) =>
        lista.asistencias.some((asist) => !!asist.idReservaGuarderia)
      )
      .map((lista) => lista.fecha)
      .filter((fecha) => fechasFiltradas.includes(fecha));
  }, [listasProyectadasOrdenadas, fechasFiltradas]);

  const fechasConGuarderiaDate = useMemo(() => {
    return fechasConGuarderia.map(f => stringADate(f));
  }, [fechasConGuarderia]);

  const fechasConHotel = useMemo(() => {
    return listasProyectadasOrdenadas
      .filter((lista) =>
        lista.asistencias.some((asist) => !!asist.idReservaHotel)
      )
      .map((lista) => lista.fecha)
      .filter((fecha) => fechasFiltradas.includes(fecha));
  }, [listasProyectadasOrdenadas, fechasFiltradas]);

  const fechasConHotelDate = useMemo(() => {
    return fechasConHotel.map(f => stringADate(f));
  }, [fechasConHotel]);

  const mascotas = useMemo(() => {
    const asistencias = listasProyectadasOrdenadas.flatMap(
      (lista) => lista.asistencias
    );
    const mascotasRepetidas = asistencias.map(
      (asistencia) =>
        ({
          id: asistencia.idMascota,
          nombre: asistencia.nombreMascota,
        } as MascotaProyectada)
    );

    const ids = new Set<string>();

    const mascotasUnicas = mascotasRepetidas.filter((mascota) => {
      if (ids.has(mascota.id)) {
        return false;
      } else {
        ids.add(mascota.id);
        return true;
      }
    });

    return mascotasUnicas;
  }, [listasProyectadasOrdenadas]);

  const error = listasAsistenciaProyectadasError;

  const onTypeAheadChanged = (selected: any[], options: any[]) => {
    if (selected && selected.length > 0) {
      const selectedValueId = selected[0]["id"];
      const validValue = options.find(
        (opt) => opt["id"] === selectedValueId
      ) as MascotaProyectada;
      setMascotaFiltro(validValue);
    } else {
      setMascotaFiltro(undefined);
    }
  };

  if (listasAsistenciaProyectadasIsLoading) {
    return <LoadingScreen isLoading={true} />;
  }

  if (error) {
    return <ServerErrorModal />;
  }

  return (
    <StyledCard>
      <StyledCardHeader className="mt-2">
        <CardTitle>{"Fechas proyectadas"}</CardTitle>
      </StyledCardHeader>
      <StyledCardBody>
        <Row className="mb-3">
          <Col sm="auto">
            <Typeahead
              placeholder="Filtre por mascota"
              id="input-mascotas"
              options={mascotas}
              labelKey="nombre"
              onChange={(selected) => onTypeAheadChanged(selected, mascotas)}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <ConvencionColores
              tipo={!!mascotaFiltro ? "TIPOS_RESERVA" : "NIVELES_OCUPACION"}
            />
          </Col>
        </Row>
        {mascotaFiltro && (
          <StyledCalendar
            selectRange={false}
            enabledDates={fechasFiltradasDate}
            dangerDates={fechasConHotelDate}
            blueDates={fechasConGuarderiaDate}
            onDateClick={onDateClick}
            disableSelection={true}
          />
        )}
        {!mascotaFiltro && (
          <StyledCalendar
            selectRange={false}
            enabledDates={fechasFiltradasDate}
            dangerDates={fechasAltasDate}
            successDates={fechasBajasDate}
            warningDates={fechasMediasDate}
            onDateClick={onDateClick}
            disableSelection={true}
          />
        )}
      </StyledCardBody>
    </StyledCard>
  );
}

type MascotaProyectada = {
  id: string;
  nombre: string;
};

function agruparFechasPorIdMascota(
  listas: ListaAsistenciaProyectada[]
): Map<string, Set<string>> {
  const agrupacion = new Map<string, Set<string>>();

  listas.forEach((lista) => {
    const fecha = lista.fecha;
    const asistencias = lista.asistencias;

    asistencias.forEach((asistencia) => {
      if (
        !esAsistenciaPasada(asistencia) ||
        (esAsistenciaPasada(asistencia) && asistencia.asistio)
      ) {
        const idMascota = asistencia.idMascota;

        if (agrupacion.has(idMascota)) {
          let fechas: Set<string> = agrupacion.get(idMascota)!!;
          fechas.add(fecha);
          agrupacion.set(idMascota, fechas);
        } else {
          let fechas = new Set<string>();
          fechas.add(fecha);
          agrupacion.set(idMascota, fechas);
        }
      }
    });
  });

  return agrupacion;
}

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background-color: inherit;
  display: flex;
  justify-content: start;
  align-items: center;
  border: 0px;
`;

const StyledCard = styled(Card)`
  margin-bottom: 1.875rem;
  background-color: #fff;
  transition: all 0.5s ease-in-out;
  position: relative;
  border: 0px solid transparent;
  border-radius: 0.25rem;
  box-shadow: 0px 0px 13px 0px rgb(82 63 105 / 5%);
`;

const StyledCardBody = styled(Card.Body)`
  padding: 1.25rem;
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border-radius: 5px;
  padding: 0%;
`;

export default CalendarSection;
